import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Autocomplete,
  TextField,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  IconButton
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';

export interface ContactTagRow {
  id: string;
  name: string;
  color: string | null;
  created_at?: string;
}

interface LeadContactTagsProps {
  contactId?: string | null;
  compact?: boolean;
  /** Atualiza lista de conversas (ex.: chips ao lado da hora). */
  onAssignmentsChanged?: () => void;
}

function tagsBase(): string {
  return `${getApiUrl()}/contact-tags`;
}

/** contact_id da conversa ou id do contato aninhado (fallback). */
export function resolveConversationContactId(
  conv?: { contact_id?: string | null; contact?: { id?: string } | null } | null
): string | null {
  if (!conv) return null;
  return conv.contact_id || conv.contact?.id || null;
}

export default function LeadContactTags({
  contactId,
  compact,
  onAssignmentsChanged
}: LeadContactTagsProps) {
  const [allTags, setAllTags] = useState<ContactTagRow[]>([]);
  const [assigned, setAssigned] = useState<ContactTagRow[]>([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadingContact, setLoadingContact] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#7986CB');
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoadingAll(true);
    try {
      const res = await axios.get(tagsBase());
      if (res.data?.success) setAllTags(res.data.data || []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Erro ao carregar tags');
    } finally {
      setLoadingAll(false);
    }
  }, []);

  const loadContact = useCallback(async () => {
    if (!contactId) {
      setAssigned([]);
      return;
    }
    setLoadingContact(true);
    try {
      const res = await axios.get(`${tagsBase()}/contacts/${contactId}`);
      if (res.data?.success) setAssigned(res.data.data || []);
    } catch {
      setAssigned([]);
    } finally {
      setLoadingContact(false);
    }
  }, [contactId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    loadContact();
  }, [loadContact]);

  const assignedIds = useMemo(() => new Set(assigned.map((t) => t.id)), [assigned]);
  const pickOptions = useMemo(
    () => allTags.filter((t) => !assignedIds.has(t.id)),
    [allTags, assignedIds]
  );

  const assignTag = async (tagId: string) => {
    if (!contactId) return;
    setError('');
    try {
      await axios.post(`${tagsBase()}/contacts/${contactId}/assign`, { tag_id: tagId });
      await Promise.all([loadContact(), loadAll()]);
      onAssignmentsChanged?.();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Não foi possível aplicar a tag');
    }
  };

  const removeTag = async (tagId: string) => {
    if (!contactId) return;
    setError('');
    try {
      await axios.delete(`${tagsBase()}/contacts/${contactId}/tags/${tagId}`);
      await loadContact();
      onAssignmentsChanged?.();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Não foi possível remover a tag');
    }
  };

  const handleCreateTag = async () => {
    const name = newName.trim().replace(/\s+/g, ' ');
    if (!name || !contactId) return;
    setSaving(true);
    setError('');
    try {
      const createRes = await axios.post(tagsBase(), {
        name,
        color: newColor || undefined
      });
      const tag = createRes.data?.data as ContactTagRow | undefined;
      const tagId = tag?.id;
      if (!tagId) throw new Error('Resposta inválida');
      await axios.post(`${tagsBase()}/contacts/${contactId}/assign`, { tag_id: tagId });
      setCreateOpen(false);
      setNewName('');
      setNewColor('#7986CB');
      await Promise.all([loadAll(), loadContact()]);
      onAssignmentsChanged?.();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Não foi possível criar a tag');
    } finally {
      setSaving(false);
    }
  };

  if (!contactId) return null;

  const busy = loadingAll || loadingContact;

  return (
    <Box sx={{ mt: compact ? 0.5 : 0.75, maxWidth: '100%' }}>
      {error && (
        <Alert
          severity="error"
          onClose={() => setError('')}
          sx={{ py: 0, mb: 0.5, fontSize: compact ? '0.75rem' : undefined }}
        >
          {error}
        </Alert>
      )}
      <Box
        sx={{
          display: 'flex',
          flexWrap: compact ? 'nowrap' : 'wrap',
          gap: 0.5,
          alignItems: 'center',
          overflowX: compact ? 'auto' : 'visible',
          pb: compact ? 0.25 : 0,
          '&::-webkit-scrollbar': { height: 4 }
        }}
      >
        {busy && assigned.length === 0 ? (
          <CircularProgress size={18} sx={{ ml: 0.5 }} />
        ) : (
          assigned.map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              size="small"
              onDelete={() => removeTag(tag.id)}
              sx={{
                height: compact ? 22 : 26,
                fontSize: compact ? '0.65rem' : '0.72rem',
                flexShrink: 0,
                borderLeft: '3px solid',
                borderColor: tag.color || '#9e9e9e',
                bgcolor: compact ? 'rgba(255,255,255,0.25)' : 'action.hover'
              }}
            />
          ))
        )}
        <Autocomplete
          size="small"
          sx={{
            minWidth: compact ? 120 : 160,
            maxWidth: compact ? 140 : 220,
            flexShrink: 0
          }}
          options={pickOptions}
          getOptionLabel={(o) => o.name}
          loading={loadingAll}
          onChange={(_, v) => {
            if (v) assignTag(v.id);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Tag…"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingAll ? <CircularProgress color="inherit" size={14} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: compact ? 28 : 32,
                  fontSize: compact ? '0.75rem' : '0.8rem',
                  ...(compact && {
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: '#000',
                    '& fieldset': { borderColor: 'rgba(0,0,0,0.25)' }
                  })
                },
                '& .MuiOutlinedInput-input': { py: 0.5 }
              }}
            />
          )}
          clearOnBlur
          selectOnFocus
          handleHomeEndKeys
        />
        <Tooltip title="Nova tag">
          <IconButton
            size="small"
            onClick={() => setCreateOpen(true)}
            sx={{
              flexShrink: 0,
              ...(compact && { color: '#000', bgcolor: 'rgba(255,255,255,0.15)' })
            }}
          >
            <AddIcon sx={{ fontSize: compact ? 18 : 22 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nova tag</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            autoFocus
            label="Nome"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Cor"
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleCreateTag} disabled={saving || !newName.trim()}>
            {saving ? <CircularProgress size={20} /> : 'Criar e aplicar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
